import { EditorState, Modifier, convertToRaw, convertFromRaw } from 'draft-js';

// ─── HTML ↔ Draft.js conversion ───────────────────────────────────────────────

/**
 * Convert Draft.js ContentState → HTML string (bold/italic/underline only)
 */
export const convertContentToHTML = (contentState) => {
    const rawContent = convertToRaw(contentState);
    let html = '';

    rawContent.blocks.forEach((block) => {
        let text = block.text;

        // Collect ranges, sort descending so we apply from right → left
        const ranges = block.inlineStyleRanges
            .map(({ offset, length, style }) => ({ start: offset, end: offset + length, style }))
            .sort((a, b) => b.start - a.start);

        ranges.forEach(({ start, end, style }) => {
            const before = text.slice(0, start);
            const mid    = text.slice(start, end);
            const after  = text.slice(end);

            if      (style === 'BOLD')      text = `${before}<b>${mid}</b>${after}`;
            else if (style === 'ITALIC')    text = `${before}<i>${mid}</i>${after}`;
            else if (style === 'UNDERLINE') text = `${before}<u>${mid}</u>${after}`;
        });

        html += text;
    });

    return html;
};

/**
 * Convert HTML string → Draft.js EditorState
 */
export const convertHTMLToEditorState = (html) => {
    if (!html) return EditorState.createEmpty();

    let plainText = '';
    const inlineStyles = [];
    let currentPos = 0;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            plainText   += node.textContent;
            currentPos  += node.textContent.length;
        } else {
            const styleMap = { B: 'BOLD', STRONG: 'BOLD', I: 'ITALIC', EM: 'ITALIC', U: 'UNDERLINE' };
            const styleName = styleMap[node.nodeName];

            if (styleName) {
                const start = currentPos;
                node.childNodes.forEach(processNode);
                inlineStyles.push({ offset: start, length: currentPos - start, style: styleName });
            } else {
                node.childNodes.forEach(processNode);
            }
        }
    };

    tempDiv.childNodes.forEach(processNode);

    const contentState = convertFromRaw({
        blocks: [{
            key: 'first-block',
            text: plainText,
            type: 'unstyled',
            depth: 0,
            inlineStyleRanges: inlineStyles,
            entityRanges: [],
            data: {},
        }],
        entityMap: {},
    });

    return EditorState.createWithContent(contentState);
};

/**
 * Render stored HTML safely in JSX
 */
export const renderHTMLContent = (htmlContent) => (
    <span dangerouslySetInnerHTML={{ __html: htmlContent }} />
);

// ─── Emoji helpers ────────────────────────────────────────────────────────────

/** Keep literal :shortcode: placeholders as-is (prevent double-conversion) */
export const sanitizeEmojiPlaceholders = (text) =>
    text.replace(/:[a-z_]+:/g, (match) => match);

// ─── Insert emoji into an EditorState ────────────────────────────────────────

/**
 * Insert an emoji at `selection` (or editor's current selection) and return
 * the new EditorState with the cursor advanced past the emoji.
 */
export const insertEmojiIntoEditor = (editorState, emoji, savedSelection = null) => {
    const contentState = editorState.getCurrentContent();
    const selection    = savedSelection || editorState.getSelection();

    const newContent = Modifier.replaceText(contentState, selection, emoji);
    const pushed     = EditorState.push(editorState, newContent, 'insert-characters');

    const newOffset  = selection.getAnchorOffset() + emoji.length;
    const newSel     = selection.merge({ anchorOffset: newOffset, focusOffset: newOffset });

    return EditorState.forceSelection(pushed, newSel);
};

// ─── Input / paste guards ─────────────────────────────────────────────────────

/**
 * handleBeforeInput factory – returns a Draft.js handleBeforeInput callback.
 * Enforces `charLimit` and (optionally) strips styles on space.
 *
 * @param {number}   charLimit
 * @param {boolean}  clearStyleOnSpace  – set true for rich-text editors
 * @param {Function} setEditorState
 */
export const makeBeforeInputHandler = (charLimit, clearStyleOnSpace, setEditorState) =>
    (chars, editorState) => {
        const len = [...editorState.getCurrentContent().getPlainText()].length;
        if (len >= charLimit) return 'handled';

        if (clearStyleOnSpace && chars === ' ') {
            const newContent = Modifier.replaceText(
                editorState.getCurrentContent(),
                editorState.getSelection(),
                ' ',
                null,           // clears all inline styles on this space
            );
            setEditorState(EditorState.push(editorState, newContent, 'insert-characters'));
            return 'handled';
        }

        return 'not-handled';
    };

/**
 * handlePastedText factory – returns a Draft.js handlePastedText callback.
 * Enforces `charLimit` and sanitises emoji shortcodes.
 *
 * @param {number}   charLimit
 * @param {Function} setEditorState
 */
export const makePasteHandler = (charLimit, setEditorState) =>
    (text, _html, editorState) => {
        const sanitized = sanitizeEmojiPlaceholders(text);
        const current   = editorState.getCurrentContent();
        const available = charLimit - [...current.getPlainText()].length;

        if (available <= 0) return 'handled';

        const trimmed    = [...sanitized].slice(0, available).join('');
        const newContent = Modifier.replaceText(current, editorState.getSelection(), trimmed);
        setEditorState(EditorState.push(editorState, newContent, 'insert-characters'));
        return 'handled';
    };